import { z } from 'zod';
import { BUILTIN_RECIPIENT_KINDS, CHANNEL_KEYS, NOTIFICATION_TRIGGER_KEYS, RECIPIENT_KINDS } from '../notifications/triggers.js';
import { HOST_ACTIONS } from '../links.js';
import { Email, IsoDateTime, Phone, Uuid, withDefaults } from './common.js';
/** Module `notifications` (docs/modules/notifications.md, ADR 0014). Owner: task S11 (S12 owns the provider settings block). */
export const NotificationTrigger = z.enum(NOTIFICATION_TRIGGER_KEYS);
export const NotificationChannel = z.enum(CHANNEL_KEYS);
export const RecipientKind = z.enum(RECIPIENT_KINDS);
/** Advanced filters; all must pass. Evaluated on the trigger context only. */
export const NotificationFilter = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('field_equals'), fieldId: Uuid, value: z.string().max(200) }),
    z.object({ kind: z.literal('field_contains'), fieldId: Uuid, value: z.string().min(1).max(200) }),
    z.object({ kind: z.literal('field_is_set'), fieldId: Uuid }),
    z.object({ kind: z.literal('host_is_set'), value: z.boolean() }),
    z.object({ kind: z.literal('method_in'), methods: z.array(z.string().max(20)).min(1).max(10) }),
    z.object({ kind: z.literal('is_member'), value: z.boolean() }),
]);
export const NotificationRecipients = z.object({
    host: z.boolean().default(false),
    member: z.boolean().default(false),
    reception: z.boolean().default(false),
    emails: z.array(Email).max(20).default([]),
    phones: z.array(Phone).max(20).default([]),
    /** webhook channel only */
    endpointId: Uuid.nullable().default(null),
    /** slack and teams channels only: an active connection of that kind (integrations module, ADR 0033) */
    connectionId: Uuid.nullable().default(null),
    // Strict: rules can't select the built-in kinds (on_site, marshals, contacts, issuer) in Phase 3; an unknown key is a 400.
}).strict();
/** Recipients as the API returns them (plain strings: the Email codec cannot be used for output). */
export const NotificationRecipientsOut = z.object({
    host: z.boolean(),
    member: z.boolean(),
    reception: z.boolean(),
    emails: z.array(z.string()),
    phones: z.array(z.string()),
    endpointId: Uuid.nullable(),
    connectionId: Uuid.nullable().optional(),
});
export const NotificationRule = z.object({
    id: Uuid,
    name: z.string(),
    enabled: z.boolean(),
    trigger: NotificationTrigger,
    /** null = every site / group */
    siteIds: z.array(Uuid).nullable(),
    groupIds: z.array(Uuid).nullable(),
    filters: z.array(NotificationFilter),
    channel: NotificationChannel,
    recipients: NotificationRecipientsOut,
    /** Templates with {{merge.fields}}; empty = the i18n default for the trigger and channel. */
    subject: z.string(),
    body: z.string(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const NotificationRuleCreate = z.object({
    name: z.string().trim().min(1).max(100),
    enabled: z.boolean().default(true),
    trigger: NotificationTrigger,
    siteIds: z.array(Uuid).max(500).nullable().default(null),
    groupIds: z.array(Uuid).max(200).nullable().default(null),
    filters: z.array(NotificationFilter).max(10).default([]),
    channel: NotificationChannel,
    recipients: NotificationRecipients,
    subject: z.string().max(200).default(''),
    /** Email: restricted HTML (sanitised on save); SMS and push: plain text. */
    body: z.string().max(10000).default(''),
});
/** Only the fields sent are changed (no defaults are applied to the ones left out). */
export const NotificationRuleUpdate = z.object({
    name: z.string().trim().min(1).max(100),
    enabled: z.boolean(),
    trigger: NotificationTrigger,
    siteIds: z.array(Uuid).max(500).nullable(),
    groupIds: z.array(Uuid).max(200).nullable(),
    filters: z.array(NotificationFilter).max(10),
    channel: NotificationChannel,
    recipients: NotificationRecipients,
    subject: z.string().max(200),
    body: z.string().max(10000),
}).partial();
export const NotificationRulePreviewRequest = z.object({ rule: NotificationRuleCreate });
export const NotificationRulePreview = z.object({
    channel: NotificationChannel,
    subject: z.string(),
    /** email: sanitised HTML; others: text */
    body: z.string(),
    /** Recipient kinds that would get it, with how many addresses (no addresses). */
    recipients: z.array(z.object({ kind: RecipientKind, count: z.number().int() })),
    warnings: z.array(z.string()),
});
export const DeliveryStatus = z.enum(['queued', 'sent', 'logged', 'skipped', 'failed']);
export const NotificationDelivery = z.object({
    id: Uuid,
    trigger: NotificationTrigger,
    source: z.enum(['rule', 'host', 'overstay', 'no_show', 'system', 'evacuation', 'lone_working', 'delivery', 'permit', 'compliance']),
    ruleIds: z.array(Uuid),
    channel: NotificationChannel,
    /** Rule kinds, plus the Phase 3 built-in kinds the log can show (rules can't choose those). */
    recipientKind: z.enum([...RECIPIENT_KINDS, ...BUILTIN_RECIPIENT_KINDS]),
    /** e.g. j***@example.com, +44 7*** ***123, "Pixel 8 (Sam T.)" */
    recipientMasked: z.string(),
    status: DeliveryStatus,
    attempts: z.number().int(),
    errorKey: z.string().nullable(),
    subjectType: z.enum(['visit', 'preregistration', 'feedback', 'evacuation', 'permit', 'lone_work_session', 'delivery', 'site']),
    /** Sent on the urgent lane (Phase 3). */
    urgent: z.boolean().optional(),
    subjectId: Uuid,
    siteId: Uuid.nullable(),
    createdAt: IsoDateTime,
    sentAt: IsoDateTime.nullable(),
});
export const NotificationDeliveryQuery = z.object({
    siteId: Uuid.optional(),
    trigger: NotificationTrigger.optional(),
    channel: NotificationChannel.optional(),
    status: DeliveryStatus.optional(),
    subjectId: Uuid.optional(),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
/** Host action page (`/h/:token`, kind host_action). GET shows, POST confirms. */
export const HostActionView = z.object({
    action: z.enum(HOST_ACTIONS),
    visitorName: z.string(),
    siteName: z.string(),
    /** When the action can no longer be taken (visit signed out, pre-registration cancelled). */
    available: z.boolean(),
    done: z.boolean(),
});
const ChannelList = z.array(z.enum(['email', 'sms', 'push'])).max(3);
/** Group settings (the HOST's repeat group): tell members when their visitors arrive or leave (spec 2.4 Notifications). */
export const NotificationsGroupSettings = z.object({
    hostAlerts: withDefaults(z.object({
        signIn: ChannelList.default(['email']),
        signOut: ChannelList.default([]),
        /** Push to the host's Companion app with "on my way" / "please wait" (only if push is available). */
        quickReplies: z.boolean().default(true),
    })),
    /** Only these fields (ids) may appear in any notification about visits to this group. Sensitive fields never do. */
    fieldAllowList: z.array(Uuid).max(50).default([]),
});
/** Site settings: who "reception" is for this site. */
export const NotificationsSiteSettings = z.object({
    reception: withDefaults(z.object({
        emails: z.array(Email).max(10).default([]),
        phones: z.array(Phone).max(10).default([]),
    })),
});
export const NotificationsTenantSettings = z.object({
    /** Delivery log kept this many days (0 = keep until the visit is deleted). */
    logRetentionDays: z.number().int().min(0).max(3650).default(90),
    /**
     * When `logRetentionDays` is 0, deliveries about subjects with no deletion event (evacuation, permit, lone working session,
     * delivery, site, feedback) are removed after this many days so they never live forever. 0 = keep. Unused while logRetentionDays > 0.
     */
    otherSubjectRetentionDays: z.number().int().min(0).max(3650).default(90),
    /** SMS costs money: off until the tenant switches it on (and a provider exists on the platform). */
    smsEnabled: z.boolean().default(false),
    /** Default country for numbers without +. */
    smsDefaultCountry: z.enum(['GB', 'IE']).default('GB'),
});
/** Platform settings (Oakford): which providers this installation uses. Owned by task S12. */
export const NotificationsPlatformSettings = z.object({
    sms: withDefaults(z.object({
        /** 'log' records what would be sent; real providers are added as new drivers. */
        provider: z.string().min(1).max(40).default('log'),
        senderId: z.string().max(11).default('CloudSign'),
        /** Values for the chosen provider's own `configSchema` (encrypted, write-only). Empty until a real provider exists. */
        providerConfig: z.record(z.string(), z.string()).default({}),
        /** Lone-worker alarms and evacuation texts keep sending when a customer has used its monthly limit (they are still counted). */
        safetyBypassesLimit: z.boolean().default(true),
    })),
    push: withDefaults(z.object({
        /** 'expo' needs the app built with the EAS project id; until then 'log'. */
        provider: z.enum(['log', 'expo', 'off']).default('log'),
        /** Optional Expo access token for "enhanced push security" (stored encrypted). Empty = not used. */
        expoAccessTokenEnc: z.string().max(2000).default(''),
    })),
});
//# sourceMappingURL=notifications.js.map